import { QUALIFICATIONS_CATEGORIES } from '../../shared/constants/qualification-categories';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { NestingQualification } from '../../shared/interfaces/quota-nesting.interface';
import {
  Criteria,
  NestedQuota,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import { RandomStringGenerator } from '../../shared/utils/random-string-generator';
import { Sets } from '../../shared/utils/sets';
import { NestedAllocation } from '../quota-nesting/domain/nested-allocation';
import { NestedCriterionDescriptionBuilder } from './nested-criterion-description-builder';
import { SurveyQuotas } from './survey-quotas';
import { DEFAULT } from '../../shared/constants/defaults';
import { NestedQuotaPercentageMechanic } from './nested-quota-percentage-mechanic';

export class NestedQuotaFilter {
  constructor(
    private readonly _quotas: QuotaPayload[],
    private readonly _selectedQualification: NestingQualification
  ) {}

  public matching(): QuotaPayload[] {
    if (this._selectedQualification.isAdvanceTargetingParent) {
      return [];
    }
    return this._matching();
  }

  private _matching(): QuotaPayload[] {
    return new SurveyQuotas(this._quotas).withAnyOfQualifications(
      this._qualificationCodesToMatch()
    );
  }

  private _qualificationCodesToMatch(): number[] {
    if (
      this._selectedQualification.qualification_code ===
      QUALIFICATION_CODES.CHILDREN
    ) {
      return [
        QUALIFICATION_CODES.CHILDREN,
        QUALIFICATION_CODES.CHILD_GENDER,
        QUALIFICATION_CODES.CHILD_AGE,
      ];
    }

    return [this._selectedQualification.qualification_code];
  }
}

export class NestedQuotaMapper {
  constructor(
    private readonly _quota: QuotaPayload,
    private readonly _selectedQualification: NestingQualification
  ) {}

  public map(): NestedQuota {
    return {
      text: this._description(),
      required_count: this._quota.required_count,
      percentage: this._quota.percentage || 0,
      minimum: this._quota.minimum || 0,
      maximum: this._quota.maximum || 0,
      criteria: this._quota.criteria,
      fielded: this._quota.fielded || 0,
    };
  }

  private _description(): string {
    return this._quota.criteria
      .reduce(
        (labels, criterion) => [
          ...labels,
          new NestedCriterionDescriptionBuilder(
            criterion,
            this._selectedQualification
          ).build(),
        ],
        <string[]>[]
      )
      .join(',');
  }
}

export class AllocatedNestedQuotaBuilder {
  constructor(
    private _quotasProduct: NestedQuota[][],
    private _surveyAllocation: number,
    private _groupId: string = DEFAULT.NESTED_GROUP_KEY
  ) {}

  public build(): NestedQuota[] {
    return this._quotasProduct.map((singleton) => {
      const percentage = this._percentage(singleton);
      const allocation = this._allocation(percentage);
      return <NestedQuota>{
        buyer_quota_id: new RandomStringGenerator().generate(),
        number: allocation,
        required_count: allocation,
        percentage: percentage,
        minimum: allocation,
        maximum: allocation,
        combinedKey: this._combinedKey(singleton),
        flexiblePer: 0,
        fielded: this._fielded(singleton),
        criteria: this._criteria(singleton),
        group_key: this._groupId,
        quota_category: QUALIFICATIONS_CATEGORIES.NESTED,
      };
    });
  }

  private _percentage(singleton: NestedQuota[]): number {
    const value = (
      singleton
        .map((s) => Number(s.percentage) / 100)
        .reduce((result, currentPercentage) => result * currentPercentage, 1) *
      100
    ).toFixed(2);
    return Math.floor(parseFloat(value));
  }

  private _allocation(percentage: number): number {
    return Math.round((this._surveyAllocation * Number(percentage)) / 100);
  }

  private _combinedKey(singleton: NestedQuota[]): string[] {
    return singleton.map((s) => s.text || '');
  }

  private _fielded(singleton: NestedQuota[]): number {
    return Math.min(...singleton.map((s) => s.fielded || 0));
  }

  private _criteria(singleton: NestedQuota[]): Criteria[] {
    return singleton.reduce(
      (result, current) => [...result, ...current.criteria],
      <Criteria[]>[]
    );
  }
}

export class NestedQuotaBuilder {
  constructor(
    private readonly _quotas: QuotaPayload[],
    private readonly _selectedQualifications: NestingQualification[],
    private readonly _surveyAllocation: number,
    private readonly _groupId: string = DEFAULT.NESTED_GROUP_KEY
  ) {}

  public build(): NestedQuota[] {
    if (!this._selectedQualifications.length) {
      return [];
    }

    const quotas = this._selectedQualifications.map((selectedQualification) => {
      const quotaPayloads = new NestedQuotaFilter(
        this._quotas,
        selectedQualification
      ).matching();
      return quotaPayloads.map((quota) =>
        new NestedQuotaMapper(quota, selectedQualification).map()
      );
    });

    const product = new Sets(quotas).cartesianProduct();
    const allocated = new AllocatedNestedQuotaBuilder(
      product,
      this._surveyAllocation,
      this._groupId
    ).build();

    /**
     * Since we are using Math.floor in the calculation of percentages, it is likely that in some cases the
     * total quota allocation will fall short of survey allocation. That's why auditing is necessary.
     **/
    const auditedNestedQuotas = new NestedAllocation(
      this._surveyAllocation,
      allocated
    ).audit();
    return new NestedQuotaPercentageMechanic(
      auditedNestedQuotas,
      this._surveyAllocation
    ).repair();
  }
}
